/**
 * The project roles of one person as editable lines, and the changes between what was loaded and what is on screen.
 * Pure: no network, no React. A line is one entry of the person on one project's team (a team_members row).
 */
import { fmtId } from '../../utils/jiraId';
import type { TeamMember } from '../../types/Team';
import type { Person, PersonProject } from '../../types/People';
import type { Workspace } from '../../utils/workspaces';

export interface RoleLine {
  /** Stable while editing; a loaded line uses its team entry id, a new one a counter. */
  key: string;
  /** The team entry on the server; null for a line that has not been saved yet. */
  teamMemberId: string | null;
  projectId: string;
  projectName: string;
  role: string;
  teams: string[];
  /** One responsibility per line of text, as the Team form shows them. */
  responsibilities: string;
  isActive: boolean;
  /** Set when this project's roster could not be read: role, responsibilities and Active are left alone. */
  loadError: string | null;
}

/** What goes to the team routes for one existing line. Only changed fields are present. */
export interface LinePatch {
  role?: string | null;
  teams?: string[];
  responsibilities?: string;
  is_active?: boolean;
}

export interface LineChanges {
  remove: RoleLine[];
  update: { line: RoleLine; patch: LinePatch }[];
  create: RoleLine[];
}

export const NO_LINE_CHANGES: LineChanges = { remove: [], update: [], create: [] };

const respText = (value: TeamMember['responsibilities'] | undefined): string =>
  Array.isArray(value) ? value.map(String).join('\n') : value ?? '';

/** The shown short reference of a project (PJ-...), as in the project list. */
export const projectRef = (projectId: string): string => fmtId('PJ', projectId);

/**
 * Lines for a person: one per project they are on. `rosters` holds each project's team (by project id) when it could
 * be read; a project missing from it gets the line marked with a load error.
 */
export function linesFromPerson(person: Person, rosters: ReadonlyMap<string, readonly TeamMember[]>): RoleLine[] {
  return person.projects.map((pr: PersonProject) => {
    const roster = rosters.get(pr.project_id);
    const member = roster?.find(m => m.id === pr.team_member_id);
    return {
      key: pr.team_member_id,
      teamMemberId: pr.team_member_id,
      projectId: pr.project_id,
      projectName: pr.project_name,
      role: member?.role ?? '',
      teams: [...(member?.teams ?? pr.teams)],
      responsibilities: respText(member?.responsibilities),
      isActive: member ? member.is_active !== false : true,
      loadError: member ? null : "Couldn't read this project's role details. Role, responsibilities and Active are left as they are.",
    };
  });
}

let counter = 0;
/** A line for a project the person is not on yet. */
export function newLine(projectId = '', projectName = ''): RoleLine {
  counter += 1;
  return { key: `new-${counter}`, teamMemberId: null, projectId, projectName, role: '', teams: [], responsibilities: '', isActive: true, loadError: null };
}

/** Projects the workspace has that no line uses yet (plus the one a given line already holds, so its own select still lists it). */
export function freeProjects(projects: readonly Workspace[], lines: readonly RoleLine[], keepForKey?: string): Workspace[] {
  const used = new Set(lines.filter(l => l.key !== keepForKey).map(l => l.projectId).filter(Boolean));
  return projects.filter(p => !used.has(p.project_id));
}

/** One sentence per problem line, by line key: no project chosen, or the same project twice. */
export function validateLines(lines: readonly RoleLine[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const seen = new Map<string, string>();
  for (const line of lines) {
    if (!line.projectId) { errors[line.key] = 'Choose a project for this line.'; continue; }
    const first = seen.get(line.projectId);
    if (first) errors[line.key] = `${line.projectName || 'This project'} is already listed. A person has one role per project.`;
    else seen.set(line.projectId, line.key);
  }
  return errors;
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const clean = (v: string) => v.trim();

/** The fields of a line that differ from what was loaded, or null when nothing changed. */
export function patchOf(initial: RoleLine, now: RoleLine): LinePatch | null {
  const patch: LinePatch = {};
  if (!now.loadError) {
    if (clean(now.role) !== clean(initial.role)) patch.role = clean(now.role) === '' ? null : clean(now.role);
    if (clean(now.responsibilities) !== clean(initial.responsibilities)) patch.responsibilities = now.responsibilities;
    if (now.isActive !== initial.isActive) patch.is_active = now.isActive;
  }
  if (!sameList(now.teams, initial.teams)) patch.teams = [...now.teams];
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * What Save has to send: lines taken out, lines changed, lines added. The project of a saved line cannot change
 * (a team entry belongs to one project), so a different project is a removed line plus a new one.
 */
export function diffLines(initial: readonly RoleLine[], now: readonly RoleLine[]): LineChanges {
  const before = new Map(initial.map(l => [l.key, l]));
  const kept = new Set(now.map(l => l.key));
  const update: LineChanges['update'] = [];
  for (const line of now) {
    const was = before.get(line.key);
    if (!was || !line.teamMemberId) continue;
    const patch = patchOf(was, line);
    if (patch) update.push({ line, patch });
  }
  return {
    remove: initial.filter(l => !kept.has(l.key)),
    update,
    create: now.filter(l => !l.teamMemberId),
  };
}

export const hasChanges = (c: LineChanges): boolean => c.remove.length + c.update.length + c.create.length > 0;

/**
 * After a Save where something failed, the lines to keep on screen: what the person typed stays, a line that was
 * added and went through now carries its server id, and a line whose removal failed comes back.
 */
export function reconcileAfterSave(
  current: readonly RoleLine[], server: readonly RoleLine[], failedRemovals: ReadonlySet<string>,
): RoleLine[] {
  const byProject = new Map(server.map(l => [l.projectId, l]));
  const out = current.map(line => {
    if (line.teamMemberId) return line;
    const saved = byProject.get(line.projectId);
    return saved ? { ...line, key: saved.key, teamMemberId: saved.teamMemberId, loadError: saved.loadError } : line;
  });
  const have = new Set(out.map(l => l.key));
  return [...out, ...server.filter(l => failedRemovals.has(l.key) && !have.has(l.key))];
}
